var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useState } from 'react';
import VpnKeyOffOutlinedIcon from '@mui/icons-material/VpnKeyOffOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useCreateProjectDropMutation } from 'root/src/client/v2/drop/hooks/useCreateProjectDropMutation';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
// A thrown-from-the-thunk error can be a legacy `{ error }` object (createProjectDrop) or a real Error.
const isLegacyError = (err) => (typeof err === 'object' && err !== null && 'error' in err);
// Shown when a creator takes a dare on a drop whose initial product (the join key) is depleted. The
// drop page checks availability before opening the key selection modal, so this replaces the key
// picker rather than appearing after a failed submit.
//
// Two ways out, because an empty pool only blocks creators who are waiting on a key. Declaring you
// already own the game takes you into the drop right away (with no join reward, now or on restock),
// and it is the only route to that declaration once the key picker is skipped. Otherwise the restock
// waitlist is a choice here: the accept-time gate still enrols creators it turns away on its own.
export const RedesignNoInitialKeysModal = memo(({ dropId, isWaiting = false, formData, onAccepted }) => {
    const modal = useRedesignModal();
    const [waiting, setWaiting] = useState(isWaiting);
    const joinWaitlistMutation = trpc.drops.joinInitialProductWaitlist.useMutation();
    const createProjectDropMutation = useCreateProjectDropMutation();
    const notify = useAsyncAction();
    const join = useAsyncAction();
    const busy = notify.loading || join.loading;
    const onNotifyMe = () => {
        void notify.run(() => joinWaitlistMutation.mutateAsync({ dropId }), {
            onSuccess: () => setWaiting(true),
            toast: {
                loading: 'Adding you to the waitlist...',
                success: 'We will email you when more keys are added',
                error: caught => (caught instanceof Error ? caught.message : 'Could not add you to the waitlist'),
            },
        });
    };
    const onAcceptWithOwnedGame = () => {
        if (!formData) {
            return;
        }
        void join.run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield createProjectDropMutation.mutateAsync(Object.assign(Object.assign({}, formData), { userHasGame: true, suppressErrorModals: true }));
        }), {
            // The success closes the modal, so no lingering tick.
            successHold: 0,
            onSuccess: () => {
                onAccepted === null || onAccepted === void 0 ? void 0 : onAccepted();
                modal.hide();
            },
            toast: {
                loading: 'Creating your dare...',
                success: 'Dare created',
                error: caught => (isLegacyError(caught) ? caught.error : caught instanceof Error ? caught.message : 'Dare cannot be created'),
            },
        });
    };
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-amber/30 bg-neon-amber/10">
					<VpnKeyOffOutlinedIcon sx={{ fontSize: 24 }} className="text-neon-amber"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Keys are gone</h2>
				<p className="text-sm text-muted-foreground">
					There are no Creator keys left for this Drop at the moment.
				</p>
				{formData && (<div className="flex w-full flex-col gap-1.5">
						<p className="text-xs text-muted-foreground">Already own the game? You can still take this dare.</p>
						<p className="text-xs text-muted-foreground">
							You will not receive a Creator key, but
							<span className="font-semibold text-neon-green"> delivering without one improves your delivery rate.</span>
						</p>
					</div>)}
				{waiting && (<p className="flex items-center gap-1.5 text-xs font-medium text-neon-green">
						<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }}/>
						You have been added to the waitlist. We will email you the moment more keys become available.
					</p>)}
			</div>

			<div className="flex w-full flex-col gap-2">
				{formData && (<Button variant="brand" onClick={onAcceptWithOwnedGame} loading={join.loading} success={join.success} error={join.error} disabled={notify.loading} className="w-full rounded-lg text-sm font-semibold">
						I already own this game
					</Button>)}
				{!waiting && (<Button variant={formData ? 'outline' : 'brand'} onClick={onNotifyMe} loading={notify.loading} success={notify.success} error={notify.error} disabled={join.loading} className="w-full rounded-lg text-sm">
						Notify me when more keys are added
					</Button>)}
				<Button variant="secondary" onClick={modal.hide} disabled={busy} className="w-full rounded-lg text-sm">
					Back
				</Button>
			</div>
		</RedesignModalShell>);
});
